import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { loadDashboardData } from "../services/api";
import type { DashboardData, DashboardDataContextValue } from "../types/api";

const DataContext = createContext<DashboardDataContextValue | null>(null);

export function DataProvider(props: { children?: React.ReactNode }) {
  const { children } = props;
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState("Initializing...");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadingProgress("Connecting to server...");

      setLoadingProgress("Fetching filters and deals...");
      const dashboardData = await loadDashboardData();

      setLoadingProgress("Processing data...");
      await new Promise((resolve) => setTimeout(resolve, 300));

      setData(dashboardData);
      setLoadingProgress("Finalizing dashboard...");
      setLoading(false);
    } catch (err) {
      let message = "Unknown error occurred";
      if (err instanceof Error) {
        message = err.message;
      } else if (
        err &&
        typeof err === "object" &&
        "toString" in err &&
        typeof err.toString === "function"
      ) {
        const stringified = err.toString();
        if (stringified && typeof stringified === "string") {
          message = stringified.replace(/^ApiError:\s*/, "");
        }
      } else if (typeof err === "string") {
        message = err;
      }
      console.error("Failed to load dashboard data:", err);
      setError(message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const value: DashboardDataContextValue = {
    data,
    loading,
    error,
    loadingProgress,
    refetch: fetchData,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData(): DashboardDataContextValue {
  const context = useContext(DataContext);

  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }

  return context;
}

export default DataContext;
